import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { UploadDetailConfigService } from './upload-detail-config.service';
import {
  UploadDetailConfig,
  ImageSubmissionData,
  ImageItem,
} from '../models/upload-config.model';
import { FormField } from '../../../../shared/components/add-form/add-form.component';

@Injectable({
  providedIn: 'root',
})
export class UploadConfigIntegrationService {
  private router = inject(Router);
  private uploadDetailConfigService = inject(UploadDetailConfigService);

  /**
   * Apply upload configuration to form fields
   * This method should be called in the component that uses app-add-form
   */
  applyUploadConfigToFormFields(formFields: FormField[]): FormField[] {
    const pathsToTry = this.getConfigPathsToTry();

    // Debug: Show all available configurations
    const allConfigs = this.uploadDetailConfigService.getAllSync();
    const updatedFields = formFields.map((field) => {
      // Apply config to image upload fields based on image type matching
      if (this.isImageUploadField(field)) {
        const imageType = this.getImageTypeFromField(field);
        const config = this.findConfigForField(field, pathsToTry);
        if (config) {
          const updatedField = this.applyConfigToField(field, config);
          return updatedField;
        }
      }
      return field;
    });

    return updatedFields;
  }

  /**
   * Get upload configuration for current path
   */
  getUploadConfigForCurrentPath(): UploadDetailConfig | null {
    // Get configurations for product-asset page
    const configs = this.uploadDetailConfigService.getByPage('product-asset');

    if (configs.length > 0) {
      return configs[0]; // Return first config found
    }

    return null;
  }

  /**
   * Check if configuration exists for current path
   */
  hasUploadConfigForCurrentPath(): boolean {
    // Check if any configurations exist for product-asset page
    const configs = this.uploadDetailConfigService.getByPage('product-asset');
    return configs.length > 0;
  }

  /**
   * Get current path from router
   */
  private getCurrentPath(): string {
    const url = this.router.url;
    // Remove query parameters and leading slash
    const pathWithoutQuery = url.split('?')[0];
    return pathWithoutQuery.substring(1);
  }

  /**
   * Get current path with leading slash (for compatibility)
   */
  private getCurrentPathWithSlash(): string {
    const url = this.router.url;
    // Remove query parameters but keep leading slash
    return url.split('?')[0];
  }

  /**
   * Get configuration paths to try in order of preference
   * This handles both specific edit configurations and fallback to add configurations
   */
  private getConfigPathsToTry(): string[] {
    const currentPath = this.getCurrentPath();
    const currentPathWithSlash = '/' + currentPath;

    // If it's an edit path, try both edit-specific config and add config as fallback
    if (currentPath.includes('/edit')) {
      const addPath = currentPath.replace('/edit', '/add');
      const addPathWithSlash = '/' + addPath;

      const paths = [
        currentPath, // Try exact edit path first
        currentPathWithSlash, // Try exact edit path with slash
        addPath, // Fallback to add path
        addPathWithSlash, // Fallback to add path with slash
      ];

      return paths;
    }

    // For non-edit paths, just try the current path
    const paths = [currentPath, currentPathWithSlash];
    return paths;
  }

  /**
   * Check if field is an image upload field
   */
  private isImageUploadField(field: FormField): boolean {
    return (
      (field.type === 'file' || field.type === 'multi-file') &&
      (field.accept?.includes('image') ||
        field.controlName.toLowerCase().includes('image') ||
        field.controlName.toLowerCase().includes('logo'))
    );
  }

  /**
   * Find configuration for a specific field based on image type matching
   */
  private findConfigForField(
    field: FormField,
    pathsToTry: string[],
  ): UploadDetailConfig | null {
    // Only apply a config that was actually configured for this field's image
    // type. Previously this fell back to *any* config for the "product-asset"
    // page when no field-specific match existed, which meant an admin-configured
    // "Detail Images" limit (e.g. maxDetailImages: 5) silently overwrote unrelated
    // fields like galleryImages' own maxFiles/maxSize.
    const imageType = this.getImageTypeFromField(field);
    if (!imageType) {
      return null;
    }

    const fieldName = this.mapImageTypeToField(imageType);
    return this.uploadDetailConfigService.getByPageAndField(
      'product-asset',
      fieldName,
    );
  }

  /**
   * Map image type to field name
   */
  private mapImageTypeToField(imageType: string): string {
    switch (imageType.toLowerCase()) {
      case 'main image':
        return 'main-image';
      case 'detail images':
        return 'detail-images';
      case 'detail image':
        return 'detail-images';
      case 'brand logo':
        return 'logo';
      case 'thumbnail':
        return 'thumbnail';
      case 'gallery image':
        return 'gallery';
      case 'product image':
        return 'product-image';
      default:
        return 'detail-images';
    }
  }

  /**
   * Get image type from field label or control name
   */
  private getImageTypeFromField(field: FormField): string | null {
    const label = field.label?.toLowerCase() || '';
    const controlName = field.controlName.toLowerCase();

    // Map field labels/names to image types
    // Check control name first as it's more reliable
    if (
      controlName.includes('mainimage') ||
      controlName.includes('main_image')
    ) {
      return 'Main Image';
    }
    if (
      controlName.includes('detailimages') ||
      controlName.includes('detail_images')
    ) {
      return 'Detail Images';
    }
    if (
      controlName.includes('detailimage') ||
      controlName.includes('detail_image')
    ) {
      return 'Detail Image';
    }
    if (controlName.includes('logo')) {
      return 'Brand Logo';
    }

    // Check label for common patterns (including translation keys)
    if (
      label.includes('main image') ||
      label.includes('mainimage') ||
      label.includes('main_image')
    ) {
      return 'Main Image';
    }
    if (
      label.includes('detail images') ||
      label.includes('detailimages') ||
      label.includes('detail_images')
    ) {
      return 'Detail Images';
    }
    if (
      label.includes('detail image') ||
      label.includes('detailimage') ||
      label.includes('detail_image')
    ) {
      return 'Detail Image';
    }
    if (label.includes('logo')) {
      return 'Brand Logo';
    }
    if (label.includes('thumbnail') || controlName.includes('thumbnail')) {
      return 'Thumbnail';
    }
    if (label.includes('gallery') || controlName.includes('gallery')) {
      return 'Gallery Image';
    }
    if (
      label.includes('product image') ||
      controlName.includes('productimage')
    ) {
      return 'Product Image';
    }

    return null;
  }

  /**
   * Check if field is a detail image field (legacy method for backward compatibility)
   */
  private isDetailImageField(field: FormField): boolean {
    const detailImageFieldNames = [
      'detailImage',
      'detailImages',
      'productImages',
      'itemImages',
      'galleryImages',
      'additionalImages',
    ];

    return (
      field.type === 'file' &&
      field.multiple === true &&
      detailImageFieldNames.some((name) =>
        field.controlName.toLowerCase().includes(name.toLowerCase()),
      )
    );
  }

  /**
   * Apply upload configuration to a specific field
   */
  private applyConfigToField(
    field: FormField,
    config: UploadDetailConfig,
  ): FormField {
    const updatedField = { ...field };

    // Apply file size limit
    if (config.fileSizeLimit && config.sizeUnit) {
      updatedField.maxSize = this.convertToBytes(
        config.fileSizeLimit,
        config.sizeUnit,
      );

      // Store a translate key (not raw English text) so instant() resolves it per active language.
      const imageType = this.getImageTypeFromField(field);
      const maxSizeMB =
        Math.round((updatedField.maxSize / (1024 * 1024)) * 100) / 100;

      updatedField.placeholder =
        imageType === 'Main Image'
          ? 'common.form.placeholders.upload_detail_config.upload_main_image_config'
          : 'common.form.placeholders.upload_detail_config.upload_detail_images_config';
      updatedField.placeholderParams = { maxSize: maxSizeMB };
    }

    // Apply accepted file formats
    if (config.fileFormat && config.fileFormat.length > 0) {
      updatedField.accept = this.mapFileFormatsToAccept(config.fileFormat);
      updatedField.acceptedTypes = this.getAcceptedTypesDescription(
        config.fileFormat,
      );
    }

    // Apply max files for detail images
    if (config.maxDetailImages) {
      // Note: The current FormField interface doesn't have a maxFiles property
      // We'll store this in a custom property that can be used by the file upload component
      (updatedField as any).maxFiles = config.maxDetailImages;
    }

    return updatedField;
  }

  /**
   * Convert size to bytes
   */
  private convertToBytes(size: number, unit: string): number {
    const multipliers: Record<string, number> = {
      KB: 1024,
      MB: 1024 * 1024,
    };

    return size * (multipliers[unit] || 1);
  }

  /**
   * Map file formats to accept attribute
   */
  private mapFileFormatsToAccept(formats: string[]): string {
    const mimeTypeMap: Record<string, string> = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      bmp: 'image/bmp',
      webp: 'image/webp',
      svg: 'image/svg+xml',
      tiff: 'image/tiff',
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ppt: 'application/vnd.ms-powerpoint',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      txt: 'text/plain',
      mp4: 'video/mp4',
      avi: 'video/x-msvideo',
      mov: 'video/quicktime',
      wmv: 'video/x-ms-wmv',
      flv: 'video/x-flv',
      webm: 'video/webm',
      mp3: 'audio/mpeg',
      wav: 'audio/wav',
      flac: 'audio/flac',
      aac: 'audio/aac',
      zip: 'application/zip',
      rar: 'application/x-rar-compressed',
      '7z': 'application/x-7z-compressed',
      tar: 'application/x-tar',
      gz: 'application/gzip',
    };

    return formats
      .map((format) => mimeTypeMap[format.toLowerCase()] || `*.${format}`)
      .join(',');
  }

  /**
   * Get human-readable accepted types description
   */
  private getAcceptedTypesDescription(formats: string[]): string {
    return formats.map((f) => f.toUpperCase()).join(', ');
  }

  /**
   * Get max files for detail images from config
   */
  getMaxFilesForDetailImages(): number | null {
    const config = this.getUploadConfigForCurrentPath();
    return config?.maxDetailImages || null;
  }

  /**
   * Get file size limit from config
   */
  getFileSizeLimit(): number | null {
    const config = this.getUploadConfigForCurrentPath();
    if (config?.fileSizeLimit && config?.sizeUnit) {
      return this.convertToBytes(config.fileSizeLimit, config.sizeUnit);
    }
    return null;
  }

  /**
   * Get accepted file formats from config
   */
  getAcceptedFileFormats(): string[] | null {
    const config = this.getUploadConfigForCurrentPath();
    return config?.fileFormat || null;
  }

  /**
   * Get all configurations synchronously (for debugging)
   */
  getAllSync(): UploadDetailConfig[] {
    return this.uploadDetailConfigService.getAllSync();
  }

  /**
   * Build image submission data with change tracking
   */
  buildImageSubmissionData(formData: any): ImageSubmissionData {
    const submissionData: ImageSubmissionData = {};

    // Process main images
    if (formData.mainImage) {
      const mainImageData = this.processImageData(formData.mainImage);
      if (mainImageData) {
        submissionData.mainImages = [mainImageData.file!];
      }
    }

    // Process detail images
    const detailImages: File[] = [];
    const removeDetailImages: string[] = [];

    // Check for indexed detail image controls (detailImage_0, detailImage_1, etc.)
    for (let i = 0; i < 20; i++) {
      // Safety limit
      const controlName = `detailImage_${i}`;
      const imageData = formData[controlName];

      if (imageData) {
        const processedData = this.processImageData(imageData);
        if (processedData) {
          if (processedData.onFileChanged && processedData.file) {
            detailImages.push(processedData.file);
          }
          if (
            processedData.isExisting &&
            !processedData.onFileChanged &&
            processedData.id
          ) {
            removeDetailImages.push(processedData.id);
          }
        }
      }
    }

    if (detailImages.length > 0) {
      submissionData.detailImages = detailImages;
    }
    if (removeDetailImages.length > 0) {
      submissionData.removeDetailImages = removeDetailImages;
    }

    return submissionData;
  }

  /**
   * Process individual image data and track changes
   */
  private processImageData(imageData: any): ImageItem | null {
    if (!imageData) return null;

    const processedData: ImageItem = {
      id: imageData.id,
      file: imageData.file,
      previewUrl: imageData.previewUrl,
      fileName: imageData.fileName,
      fileSize: imageData.fileSize,
      isExisting: imageData.isExisting,
      onFileChanged: imageData.onFileChanged || false,
    };

    // If it's a new file upload, mark as changed
    if (imageData.file && imageData.file instanceof File) {
      processedData.onFileChanged = true;
    }

    return processedData;
  }

  /**
   * Mark image as changed when user uploads a new file
   */
  markImageAsChanged(imageData: any): any {
    if (imageData) {
      return {
        ...imageData,
        onFileChanged: true,
      };
    }
    return imageData;
  }

  /**
   * Mark image for removal when user deletes it
   */
  markImageForRemoval(imageData: any): any {
    if (imageData?.isExisting) {
      return {
        ...imageData,
        onFileChanged: false,
        removed: true,
      };
    }
    return null;
  }
}
